import {
  Component,
  ElementRef,
  HostListener,
  ViewChild,
  computed,
  signal,
} from '@angular/core';
import { ImageEnum } from '../../utils/image.enum';

type CalculatorOperator = '+' | '-' | '*' | '/';

interface CalculatorButton {
  label: string;
  action:
    | 'digit'
    | 'operator'
    | 'memory-clear'
    | 'memory-recall'
    | 'memory-store'
    | 'memory-add'
    | 'memory-subtract'
    | 'backspace'
    | 'clear'
    | 'toggle-sign'
    | 'sqrt'
    | 'percent'
    | 'reciprocal'
    | 'decimal'
    | 'equals';
  value?: string;
  className?: string;
  gridClass?: string;
}

@Component({
  selector: 'app-calculator-widget',
  imports: [],
  templateUrl: './calculator-widget.html',
  styleUrl: './calculator-widget.css',
})
export class CalculatorWidgetComponent {
  @ViewChild('launcherButton') private launcherButton?: ElementRef<HTMLButtonElement>;
  @ViewChild('calculatorPanel') private calculatorPanel?: ElementRef<HTMLDivElement>;

  protected readonly isOpen = signal(false);
  protected readonly panelPosition = signal({ x: 0, y: 0 });
  protected readonly displayValue = signal('0');
  protected readonly expressionValue = signal('');
  protected readonly memoryValue = signal<number | null>(null);
  protected readonly memoryIndicator = computed(() => (this.memoryValue() !== null ? 'M' : ''));
  protected readonly memoryButtons: CalculatorButton[] = [
    { label: 'MC', action: 'memory-clear' },
    { label: 'MR', action: 'memory-recall' },
    { label: 'MS', action: 'memory-store' },
    { label: 'M+', action: 'memory-add' },
    { label: 'M-', action: 'memory-subtract' },
  ];
  protected readonly imageEnum = ImageEnum;
  protected readonly keypadRows: CalculatorButton[][] = [
    [
      {
        label: '←',
        action: 'backspace',
        className: 'calculator-action calculator-action-warm',
        gridClass: 'calculator-grid-span-2',
      },
      {
        label: 'C',
        action: 'clear',
        className: 'calculator-action calculator-action-warm',
      },
      { label: '+/-', action: 'toggle-sign', className: 'calculator-action calculator-action-warm' },
      { label: '√', action: 'sqrt', className: 'calculator-key--muted' },
    ],
    [
      { label: '7', action: 'digit', value: '7' },
      { label: '8', action: 'digit', value: '8' },
      { label: '9', action: 'digit', value: '9' },
      { label: '/', action: 'operator', value: '/' },
      { label: '%', action: 'percent' },
    ],
    [
      { label: '4', action: 'digit', value: '4' },
      { label: '5', action: 'digit', value: '5' },
      { label: '6', action: 'digit', value: '6' },
      { label: '*', action: 'operator', value: '*' },
      { label: '1/x', action: 'reciprocal' },
    ],
    [
      { label: '1', action: 'digit', value: '1' },
      { label: '2', action: 'digit', value: '2' },
      { label: '3', action: 'digit', value: '3' },
      { label: '-', action: 'operator', value: '-' },
      { label: '=', action: 'equals', className: 'calculator-equals', gridClass: 'calculator-grid-equals' },
    ],
    [
      { label: '0', action: 'digit', value: '0', className: 'calculator-key--double' },
      { label: '.', action: 'decimal' },
      { label: '+', action: 'operator', value: '+' },
    ],
  ];

  private storedValue: number | null = null;
  private pendingOperator: CalculatorOperator | null = null;
  private waitingForNextOperand = false;
  private hasPanelPosition = false;
  private isDragging = false;
  private dragOffsetX = 0;
  private dragOffsetY = 0;

  protected toggleCalculator(): void {
    const nextOpenState = !this.isOpen();
    this.isOpen.set(nextOpenState);

    if (nextOpenState) {
      this.setInitialPosition();
    }
  }

  protected closeCalculator(): void {
    this.isOpen.set(false);
  }

  protected getButtonClasses(button: CalculatorButton): string {
    return ['calculator-key', button.className ?? '', button.gridClass ?? ''].filter(Boolean).join(' ');
  }

  protected startDragging(event: PointerEvent): void {
    const target = event.target as HTMLElement | null;

    if (target?.closest('button')) {
      return;
    }

    const panel = this.calculatorPanel?.nativeElement;

    if (!panel) {
      return;
    }

    const rect = panel.getBoundingClientRect();
    this.isDragging = true;
    this.dragOffsetX = event.clientX - rect.left;
    this.dragOffsetY = event.clientY - rect.top;
    panel.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  }

  protected handleButtonClick(button: CalculatorButton): void {
    if (button.action === 'digit') {
      this.inputDigit(button.value ?? '');
      return;
    }

    if (button.action === 'operator') {
      this.setOperator((button.value ?? '+') as CalculatorOperator);
      return;
    }

    if (button.action === 'memory-clear') {
      this.memoryValue.set(null);
      return;
    }

    if (button.action === 'memory-recall') {
      if (this.memoryValue() !== null) {
        this.displayValue.set(this.formatNumber(this.memoryValue() ?? 0));
        this.waitingForNextOperand = true;
      }
      return;
    }

    if (button.action === 'memory-store') {
      this.memoryValue.set(this.getDisplayNumber());
      return;
    }

    if (button.action === 'memory-add') {
      this.memoryValue.set((this.memoryValue() ?? 0) + this.getDisplayNumber());
      return;
    }

    if (button.action === 'memory-subtract') {
      this.memoryValue.set((this.memoryValue() ?? 0) - this.getDisplayNumber());
      return;
    }

    if (button.action === 'backspace') {
      this.backspace();
      return;
    }

    if (button.action === 'clear') {
      this.clearAll();
      return;
    }

    if (button.action === 'toggle-sign') {
      this.toggleSign();
      return;
    }

    if (button.action === 'sqrt') {
      this.applyUnaryOperation((value) => {
        if (value < 0) {
          return null;
        }

        return Math.sqrt(value);
      }, 'sqrt');
      return;
    }

    if (button.action === 'percent') {
      this.applyPercent();
      return;
    }

    if (button.action === 'reciprocal') {
      this.applyUnaryOperation((value) => {
        if (value === 0) {
          return null;
        }

        return 1 / value;
      }, '1/');
      return;
    }

    if (button.action === 'decimal') {
      this.inputDecimal();
      return;
    }

    if (button.action === 'equals') {
      this.evaluate();
    }
  }

  @HostListener('document:pointermove', ['$event'])
  protected onPointerMove(event: PointerEvent): void {
    if (!this.isDragging || !this.isOpen()) {
      return;
    }

    this.panelPosition.set(this.clampPosition(
      event.clientX - this.dragOffsetX,
      event.clientY - this.dragOffsetY,
    ));
  }

  @HostListener('document:pointerup')
  protected onPointerUp(): void {
    this.isDragging = false;
  }

  @HostListener('window:resize')
  protected onWindowResize(): void {
    if (!this.isOpen()) {
      return;
    }

    const { x, y } = this.panelPosition();
    this.panelPosition.set(this.clampPosition(x, y));
  }

  private setInitialPosition(): void {
    queueMicrotask(() => {
      const launcherRect = this.launcherButton?.nativeElement.getBoundingClientRect();
      const panelRect = this.calculatorPanel?.nativeElement.getBoundingClientRect();
      const panelWidth = panelRect?.width ?? 262;
      const panelHeight = panelRect?.height ?? 390;

      if (!this.hasPanelPosition && launcherRect) {
        const shouldOpenIntoQuestionArea = window.innerWidth < 1360;
        const preferredX = shouldOpenIntoQuestionArea
          ? launcherRect.left - panelWidth - 12
          : launcherRect.right - panelWidth;
        const preferredY = shouldOpenIntoQuestionArea
          ? Math.max(launcherRect.top + 12, 8)
          : launcherRect.bottom + 8;
        this.panelPosition.set(this.clampPosition(preferredX, preferredY, panelWidth, panelHeight));
        this.hasPanelPosition = true;
        return;
      }

      const { x, y } = this.panelPosition();
      this.panelPosition.set(this.clampPosition(x, y, panelWidth, panelHeight));
    });
  }

  private inputDigit(value: string): void {
    if (!value) {
      return;
    }

    const current = this.displayValue();

    if (this.waitingForNextOperand) {
      this.displayValue.set(value);
      this.waitingForNextOperand = false;
      return;
    }

    if (current === '0') {
      this.displayValue.set(value);
      return;
    }

    this.displayValue.set(`${current}${value}`);
  }

  private inputDecimal(): void {
    if (this.waitingForNextOperand) {
      this.displayValue.set('0.');
      this.waitingForNextOperand = false;
      return;
    }

    if (!this.displayValue().includes('.')) {
      this.displayValue.set(`${this.displayValue()}.`);
    }
  }

  private setOperator(operator: CalculatorOperator): void {
    const currentValue = this.getDisplayNumber();

    if (this.pendingOperator && this.storedValue !== null && !this.waitingForNextOperand) {
      const result = this.performCalculation(this.storedValue, currentValue, this.pendingOperator);

      if (result === null) {
        this.showError();
        return;
      }

      this.storedValue = result;
      this.displayValue.set(this.formatNumber(result));
    } else {
      this.storedValue = currentValue;
    }

    this.pendingOperator = operator;
    this.expressionValue.set(`${this.formatNumber(this.storedValue ?? currentValue)} ${operator}`);
    this.waitingForNextOperand = true;
  }

  private evaluate(): void {
    if (this.pendingOperator === null || this.storedValue === null) {
      this.expressionValue.set(this.displayValue());
      return;
    }

    const currentValue = this.getDisplayNumber();
    const result = this.performCalculation(this.storedValue, currentValue, this.pendingOperator);

    if (result === null) {
      this.showError();
      return;
    }

    this.expressionValue.set(
      `${this.formatNumber(this.storedValue)} ${this.pendingOperator} ${this.formatNumber(currentValue)} =`,
    );
    this.displayValue.set(this.formatNumber(result));
    this.storedValue = null;
    this.pendingOperator = null;
    this.waitingForNextOperand = true;
  }

  private applyPercent(): void {
    const currentValue = this.getDisplayNumber();
    const nextValue = this.storedValue !== null && this.pendingOperator
      ? (this.storedValue * currentValue) / 100
      : currentValue / 100;

    this.displayValue.set(this.formatNumber(nextValue));
  }

  private applyUnaryOperation(transform: (value: number) => number | null, label: string): void {
    const currentValue = this.getDisplayNumber();
    const result = transform(currentValue);

    if (result === null || !Number.isFinite(result)) {
      this.showError();
      return;
    }

    this.expressionValue.set(`${label}(${this.formatNumber(currentValue)})`);
    this.displayValue.set(this.formatNumber(result));
    this.waitingForNextOperand = true;
  }

  private toggleSign(): void {
    const currentValue = this.getDisplayNumber();

    if (currentValue === 0) {
      return;
    }

    this.displayValue.set(this.formatNumber(currentValue * -1));
  }

  private backspace(): void {
    if (this.waitingForNextOperand) {
      return;
    }

    const current = this.displayValue();

    if (current.length <= 1 || (current.length === 2 && current.startsWith('-'))) {
      this.displayValue.set('0');
      return;
    }

    this.displayValue.set(current.slice(0, -1));
  }

  private clearAll(): void {
    this.displayValue.set('0');
    this.expressionValue.set('');
    this.storedValue = null;
    this.pendingOperator = null;
    this.waitingForNextOperand = false;
  }

  private performCalculation(left: number, right: number, operator: CalculatorOperator): number | null {
    if (operator === '+') {
      return left + right;
    }

    if (operator === '-') {
      return left - right;
    }

    if (operator === '*') {
      return left * right;
    }

    if (right === 0) {
      return null;
    }

    return left / right;
  }

  private showError(): void {
    this.displayValue.set('Error');
    this.expressionValue.set('');
    this.storedValue = null;
    this.pendingOperator = null;
    this.waitingForNextOperand = true;
  }

  private getDisplayNumber(): number {
    const parsedValue = Number(this.displayValue());
    return Number.isFinite(parsedValue) ? parsedValue : 0;
  }

  private formatNumber(value: number): string {
    if (!Number.isFinite(value)) {
      return 'Error';
    }

    const roundedValue = Math.round(value * 1_000_000_000) / 1_000_000_000;
    return `${roundedValue}`.replace(/\.0$/, '');
  }

  private clampPosition(
    x: number,
    y: number,
    width = this.calculatorPanel?.nativeElement.getBoundingClientRect().width ?? 262,
    height = this.calculatorPanel?.nativeElement.getBoundingClientRect().height ?? 390,
  ): { x: number; y: number } {
    const maxX = Math.max(window.innerWidth - width - 8, 8);
    const maxY = Math.max(window.innerHeight - height - 8, 8);

    return {
      x: Math.min(Math.max(8, x), maxX),
      y: Math.min(Math.max(8, y), maxY),
    };
  }
}
